import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";

const EditSessionDateModal = ({ show, onHide, session, onSuccess }) => {
  const [newDate, setNewDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (session) {
      setNewDate(new Date(session.sessionDate));
      setError(null);
      setSuccess(false);
    }
  }, [session?._id]); // Only re-run when session ID changes, not on every session object change

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!newDate || !session) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const authFetch = axios.create({
        baseURL: "/api/v1",
      });

      const response = await authFetch.patch(`/sessions/${session._id}/date`, {
        newDate: newDate.toISOString(),
      });

      setSuccess(true);
      onSuccess(response.data);

      // Close modal after showing success briefly
      timeoutRef.current = setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update session date");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!session) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Session Date</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label><strong>Filename</strong></Form.Label>
          <Form.Control
            type="text"
            value={session.filename || "N/A"}
            disabled
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label><strong>Current Date</strong></Form.Label>
          <Form.Control
            type="text"
            value={format(new Date(session.sessionDate), "dd MMM yyyy")}
            disabled
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label><strong>New Date</strong></Form.Label>
          <div>
            <DatePicker
              selected={newDate}
              onChange={(date) => setNewDate(date)}
              dateFormat="dd MMM yyyy"
              className="form-control"
              maxDate={new Date()}
            />
          </div>
        </Form.Group>

        {session.numberOfShots > 0 && (
          <div className="alert alert-warning" role="alert">
            <strong>Note:</strong> This will update the date on{" "}
            <strong>{session.numberOfShots}</strong> shot
            {session.numberOfShots !== 1 ? "s" : ""} in this session.
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success!</strong> Session date updated.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting || success}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !newDate || success}
        >
          {isSubmitting ? "Updating..." : success ? "Updated!" : "Update Date"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSessionDateModal;
